/**
 * Inline Editor — shared editable textarea used by all visualizers in edit mode.
 *
 * Shows a textarea with draft state, dirty tracking, Revert/Apply buttons,
 * and Ctrl+Enter commit. Used as the edit-mode fallback for every visualizer tool.
 */

import React from 'react';

export interface InlineEditorProps {
  input: string;
  onEdit?: (newData: string) => void;
  wordWrap?: boolean;
  fontSize?: number;
}

export const InlineEditor: React.FC<InlineEditorProps> = ({
  input,
  onEdit,
  wordWrap = true,
  fontSize = 13,
}) => {
  const [draft, setDraft] = React.useState(input);
  const [isDirty, setIsDirty] = React.useState(false);

  React.useEffect(() => {
    setDraft(input);
    setIsDirty(false);
  }, [input]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(e.target.value);
    setIsDirty(e.target.value !== input);
  };

  const handleCommit = () => {
    if (onEdit && isDirty) {
      onEdit(draft);
      setIsDirty(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleCommit();
    }
  };

  const handleRevert = () => {
    setDraft(input);
    setIsDirty(false);
  };

  const lineCount = draft.split('\n').length;
  const charCount = draft.length;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>{lineCount} lines</span>
          <span>{charCount} chars</span>
          {isDirty && (
            <span className="text-yellow-500 font-medium">● Modified</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-40"
            onClick={handleRevert}
            disabled={!isDirty}
          >
            Revert
          </button>
          <button
            type="button"
            className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
            onClick={handleCommit}
            disabled={!isDirty}
          >
            Apply (Ctrl+Enter)
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <textarea
          className="w-full h-full p-3 bg-background text-foreground font-mono resize-none focus:outline-none"
          style={{
            fontSize: `${fontSize}px`,
            lineHeight: '1.5',
            whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
            overflowWrap: wordWrap ? 'break-word' : 'normal',
            tabSize: 2,
          }}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          spellCheck={false}
        />
      </div>
    </div>
  );
};
